import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  loginWithLambda,
  signupWithLambda,
  AuthTokens,
  SignupResponse,
} from "../repository/authRepository";
import { setupAutoRefresh, clearAutoRefresh, isTokenExpired } from "../utils/tokenRefresh";

interface User {
  uuid: string;
  username: string;
  email: string;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, email: string, password1: string, password2: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function storeSession(tokens: { access: string; refresh: string; user: User }) {
  localStorage.setItem("access_token", tokens.access);
  localStorage.setItem("refresh_token", tokens.refresh);
  localStorage.setItem("user", JSON.stringify(tokens.user));
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const raw = localStorage.getItem("user");
    const hasRefresh = localStorage.getItem("refresh_token");
    if (raw && hasRefresh && !isTokenExpired(0)) {
      try {
        setUser(JSON.parse(raw));
        setupAutoRefresh();
      } catch {
        /* ignore corrupt user blob */
      }
    } else if (raw && hasRefresh) {
      // access token expired but we may still have a valid refresh token
      try {
        setUser(JSON.parse(raw));
        setupAutoRefresh();
      } catch {
        /* ignore */
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const result = await loginWithLambda({ username, password });
    if ("error" in result) {
      throw new Error(result.error);
    }
    const tokens = result as AuthTokens;
    storeSession({ access: tokens.access, refresh: tokens.refresh, user: tokens.user });
    setUser(tokens.user);
    setupAutoRefresh();
  }, []);

  const signup = useCallback(
    async (username: string, email: string, password1: string, password2: string) => {
      const result = await signupWithLambda({
        username,
        email,
        password1,
        password2,
        terms_of_service: true,
      });
      if ("error" in result) {
        throw new Error(result.error);
      }
      const resp = result as SignupResponse;
      if (resp.access && resp.refresh && resp.user) {
        storeSession({
          access: resp.access,
          refresh: resp.refresh,
          user: resp.user as User,
        });
        setUser(resp.user as User);
        setupAutoRefresh();
      } else {
        // signup succeeded without auto-login tokens; fall back to logging in
        await login(username, password1);
      }
    },
    [login]
  );

  const logout = useCallback(() => {
    clearAutoRefresh();
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, signup, logout }),
    [user, loading, login, signup, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
